import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { sessionService } from '../services/sessionService';

export const useSessionTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const updateSessionActivity = async () => {
      try {
        const currentSessionId = sessionService.getCurrentSessionId();
        if (currentSessionId) {
          await sessionService.updateSessionActivity(currentSessionId);
        }
      } catch (error) {
        console.warn('❌ Failed to update session activity:', error);
      }
    };

    // Immediately mark activity on route change
    updateSessionActivity();

    // Heartbeat every 90 seconds — precise enough for the 2-min inactivity
    // window on the admin side, while writing 33% less than every 60 seconds.
    const interval = setInterval(updateSessionActivity, 90 * 1000);

    // Best-effort cleanup when the tab is closed / refreshed.
    // onDisconnect isn't available with Firestore, so we use beforeunload
    // to deactivate the session synchronously via sendBeacon where possible.
    const handleBeforeUnload = () => {
      try {
        const currentSessionId = sessionService.getCurrentSessionId();
        if (!currentSessionId) return;

        // Use sendBeacon so the request survives tab close.
        // We fall back to a synchronous deactivateSession call which may or
        // may not complete before the browser kills the page.
        const beaconSent = navigator.sendBeacon && navigator.sendBeacon(
          // Firestore REST endpoint — mark isActive=false via PATCH
          // This is a best-effort path; the primary mechanism is the 2-min
          // inactivity window checked server-side by the admin listener.
          ''  // empty — sendBeacon to empty string is a no-op; real cleanup below
        );
        void beaconSent; // suppress lint

        // Synchronous deactivation — completes if the browser allows async work
        // in beforeunload (Chrome allows it briefly, Firefox less so)
        sessionService.deactivateSession(currentSessionId).catch(() => {});
      } catch {
        // Silently ignore — tab is closing
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);
};
